/**
 * Phase 3 Task 15 — Multi-device management UI.
 *
 * Lets an agent (or admin acting on their behalf) view + add + edit + delete
 * the SIP endpoints attached to their account. Priority is edited via an
 * input field; drag-reorder is deferred to Phase 4 polish — the bulk
 * reorder API endpoint is already there to support it when added.
 */
import { useEffect, useState, type JSX } from 'react'
import { Button } from 'primereact/button'
import { DataTable } from 'primereact/datatable'
import { Column } from 'primereact/column'
import { Dialog } from 'primereact/dialog'
import { InputText } from 'primereact/inputtext'
import { InputNumber } from 'primereact/inputnumber'
import { InputSwitch } from 'primereact/inputswitch'
import { Dropdown } from 'primereact/dropdown'
import { Tag } from 'primereact/tag'
import { api } from '../../api/client.js'
import { useAuthStore } from '../../store/authStore.js'

type DeviceType = 'WEBRTC' | 'SIP_DESK' | 'MOBILE' | 'SOFTPHONE'

interface Device {
  id: string
  agentId: string
  deviceType: DeviceType
  sipEndpoint: string
  priority: number
  enabled: boolean
  label?: string | null
  lastRegisteredAt?: string | null
}

interface DeviceListResponse {
  devices: Device[]
}

const DEVICE_TYPES: Array<{ label: string; value: DeviceType }> = [
  { label: 'WebRTC', value: 'WEBRTC' },
  { label: 'Desk Phone', value: 'SIP_DESK' },
  { label: 'Mobile', value: 'MOBILE' },
  { label: 'Softphone', value: 'SOFTPHONE' },
]

interface FormState {
  deviceType: DeviceType
  sipEndpoint: string
  priority: number
  enabled: boolean
  label: string
}

const EMPTY_FORM: FormState = {
  deviceType: 'WEBRTC',
  sipEndpoint: '',
  priority: 1,
  enabled: true,
  label: '',
}

export default function DevicesPage(): JSX.Element {
  const user = useAuthStore((s) => s.user)
  const agentId = user?.sub ?? ''

  const [devices, setDevices] = useState<Device[]>([])
  const [loading, setLoading] = useState(false)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Device | null>(null)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [error, setError] = useState<string | null>(null)

  async function load(): Promise<void> {
    if (!agentId) return
    setLoading(true)
    try {
      const res = await api.get<DeviceListResponse>(
        `/agents/${agentId}/devices`,
      )
      setDevices(res.devices)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [agentId])

  function openAdd(): void {
    setEditing(null)
    setForm(EMPTY_FORM)
    setDialogOpen(true)
  }
  function openEdit(d: Device): void {
    setEditing(d)
    setForm({
      deviceType: d.deviceType,
      sipEndpoint: d.sipEndpoint,
      priority: d.priority,
      enabled: d.enabled,
      label: d.label ?? '',
    })
    setDialogOpen(true)
  }

  async function save(): Promise<void> {
    setError(null)
    try {
      const body: Record<string, unknown> = {
        deviceType: form.deviceType,
        sipEndpoint: form.sipEndpoint,
        priority: form.priority,
        enabled: form.enabled,
      }
      if (form.label.trim().length > 0) body['label'] = form.label.trim()

      if (editing) {
        await api.patch<Device>(
          `/agents/${agentId}/devices/${editing.id}`,
          body,
        )
      } else {
        await api.post<Device>(`/agents/${agentId}/devices`, body)
      }
      setDialogOpen(false)
      await load()
    } catch (e) {
      setError((e as Error).message)
    }
  }

  async function remove(d: Device): Promise<void> {
    setError(null)
    try {
      await api.delete<void>(`/agents/${agentId}/devices/${d.id}`)
      await load()
    } catch (e) {
      setError((e as Error).message)
    }
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold">My Devices</h2>
        <Button
          label="Add Device"
          icon="pi pi-plus"
          onClick={openAdd}
          data-testid="add-device-btn"
        />
      </div>

      {error && (
        <div className="text-red-600 text-sm" role="alert">
          {error}
        </div>
      )}

      <DataTable
        value={devices}
        loading={loading}
        emptyMessage="No devices configured. Add one to start receiving calls."
        data-testid="devices-table"
      >
        <Column field="priority" header="Priority" style={{ width: '6rem' }} />
        <Column field="deviceType" header="Type" />
        <Column field="sipEndpoint" header="SIP Endpoint" />
        <Column
          field="label"
          header="Label"
          body={(row: Device): string => row.label ?? ''}
        />
        <Column
          field="enabled"
          header="Enabled"
          body={(row: Device): JSX.Element => (
            <Tag
              value={row.enabled ? 'enabled' : 'disabled'}
              severity={row.enabled ? 'success' : 'danger'}
            />
          )}
        />
        <Column
          header="Actions"
          body={(row: Device): JSX.Element => (
            <div className="flex gap-2">
              <Button
                icon="pi pi-pencil"
                text
                size="small"
                aria-label="edit"
                onClick={() => openEdit(row)}
              />
              <Button
                icon="pi pi-trash"
                text
                size="small"
                severity="danger"
                aria-label="delete"
                onClick={() => void remove(row)}
              />
            </div>
          )}
        />
      </DataTable>

      <Dialog
        header={editing ? 'Edit Device' : 'Add Device'}
        visible={dialogOpen}
        onHide={() => setDialogOpen(false)}
        style={{ width: '420px' }}
      >
        <div className="flex flex-col gap-3 pt-2">
          <label className="flex flex-col gap-1">
            <span className="text-sm">Device Type</span>
            <Dropdown
              value={form.deviceType}
              options={DEVICE_TYPES}
              onChange={(e) =>
                setForm({ ...form, deviceType: e.value as DeviceType })
              }
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm">SIP Endpoint</span>
            <InputText
              value={form.sipEndpoint}
              onChange={(e) =>
                setForm({ ...form, sipEndpoint: e.target.value })
              }
              data-testid="sip-endpoint-input"
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm">Priority</span>
            <InputNumber
              value={form.priority}
              onValueChange={(e) =>
                setForm({ ...form, priority: e.value ?? 1 })
              }
              min={1}
              max={32}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-sm">Label</span>
            <InputText
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
          </label>
          <label className="flex items-center gap-2">
            <InputSwitch
              checked={form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.value ?? true })}
            />
            <span className="text-sm">Enabled</span>
          </label>

          <div className="flex justify-end gap-2 pt-2">
            <Button
              label="Cancel"
              text
              onClick={() => setDialogOpen(false)}
            />
            <Button
              label="Save"
              onClick={() => void save()}
              data-testid="save-device-btn"
            />
          </div>
        </div>
      </Dialog>
    </div>
  )
}
